import { cn } from '@/lib/utils';

interface TableItem {
  key: string;
  value: React.ReactNode;
  hasHoverEffect?: boolean;
}

export interface TableProps {
  variant?: 'extra-left-column' | 'default';
  head: TableItem[];
  body: TableItem[][];
  footer?: TableItem[];
}

const isFirstRow = (rowIndex: number) => rowIndex === 0;
const isLastRow = (rowIndex: number, body: TableItem[][]) =>
  rowIndex === body.length - 1;
const isFirstColumn = (columnIndex: number) => columnIndex === 0;
const isLastColumn = (columnIndex: number, row: TableItem[]) =>
  columnIndex === row.length - 1;

export function Table({ head, body, footer, variant = 'default' }: TableProps) {
  const columnsCount = head.length;

  const variantClasses = {
    'extra-left-column': {
      gridTemplateColumns: `230px repeat(${columnsCount},minmax(185px,1fr))`,
    },
    default: {
      gridTemplateColumns: `repeat(${columnsCount},minmax(185px,1fr))`,
    },
  }[variant];

  return (
    <div className='w-full overflow-auto'>
      <div
        className='grid w-full'
        style={variantClasses}
        data-tracker-component='table-header'
      >
        {variant === 'extra-left-column' && (
          <TableHeaderItem
            classNames={{ root: 'border-transparent bg-transparent' }}
          />
        )}
        {head.map((column, index) => (
          <TableHeaderItem
            key={column.key}
            hasHoverEffect={column.hasHoverEffect}
            classNames={{
              root: cn(
                isFirstColumn(index) && 'rounded-tl',
                isLastColumn(index, head) && 'rounded-tr border-r'
              ),
            }}
          >
            {column.value}
          </TableHeaderItem>
        ))}
      </div>
      <div data-tracker-component='table-content'>
        {body.map((row, rowIndex) => (
          <div
            key={row[0].key}
            className='group grid w-full'
            style={variantClasses}
          >
            {row.map((column, columnIndex) => (
              <TableItem
                key={column.key}
                hasHoverEffect={column.hasHoverEffect}
                classNames={{
                  root: cn(
                    isFirstColumn(columnIndex) && 'text-left',
                    isLastColumn(columnIndex, row) && 'border-r',
                    !isFirstRow(rowIndex)
                      ? 'border-t-transparent group-hover:border-t'
                      : 'border-t',
                    // variant === 'extra-left-column'
                    isFirstRow(rowIndex) &&
                      isFirstColumn(columnIndex) &&
                      variant === 'extra-left-column' &&
                      'rounded-tl border-t',
                    isLastRow(rowIndex, body) &&
                      isFirstColumn(columnIndex) &&
                      variant === 'extra-left-column' &&
                      'rounded-bl',
                    // variant === 'default'
                    variant === 'default' &&
                      isLastRow(rowIndex, body) &&
                      isFirstColumn(columnIndex) &&
                      'rounded-bl',
                    variant === 'default' &&
                      isLastRow(rowIndex, body) &&
                      isLastColumn(columnIndex, row) &&
                      'rounded-br border-r'
                  ),
                }}
              >
                {column.value}
              </TableItem>
            ))}
          </div>
        ))}
      </div>
      {variant === 'extra-left-column' && (
        <div
          className='grid w-full'
          style={variantClasses}
          data-tracker-component='table-footer'
        >
          <TableItem
            classNames={{ root: 'border-transparent bg-transparent' }}
          />
          {footer?.map((column, index) => (
            <TableItem
              key={column.key}
              classNames={{
                root: cn(
                  isFirstColumn(index) && 'rounded-bl',
                  isLastColumn(index, footer) && 'rounded-br border-r'
                ),
              }}
              hasHoverEffect={column.hasHoverEffect}
            >
              {column.value}
            </TableItem>
          ))}
        </div>
      )}
    </div>
  );
}

type TableItemProps = {
  children?: React.ReactNode;
  classNames?: {
    root?: string;
  };
  hasHoverEffect?: boolean;
};

function TableItem({
  children,
  classNames,
  hasHoverEffect = true,
}: TableItemProps) {
  return (
    <div
      className={cn(
        'bg-base-white flex gap-3 border-b border-l border-t border-solid border-gray-200',
        'px-2 py-6 md:px-6 md:py-6',
        hasHoverEffect &&
          'group-hover:bg-brand-500/[0.08] group-hover:border-brand-500',
        classNames?.root
      )}
    >
      {children}
    </div>
  );
}

function TableHeaderItem({
  children,
  classNames,
  hasHoverEffect = false,
}: TableItemProps) {
  return (
    <div
      className={cn(
        'text-subtitle-l-d bold bg-base-white flex gap-3 border-l border-t border-solid border-gray-200 font-serif',
        'px-2 py-6 md:px-6 md:py-6',
        hasHoverEffect &&
          'group-hover:bg-brand-500/[0.08] group-hover:border-brand-500',
        classNames?.root
      )}
    >
      {children}
    </div>
  );
}
